/**
 * Thin wrappers around the Store API + WP REST endpoints. Each function maps
 * to a single HTTP call and returns the parsed response body. Higher layers
 * (hooks, contexts) compose these.
 *
 * No business logic here — keep it stupid-simple so the API surface is easy
 * to mock and to swap.
 */
import wooClient from './wooClient';
import { wpClient } from './wpClient';
import { parseWooError } from '../errors';

// =====================================================================
// Store API — products
// =====================================================================

export async function getProducts(params = {}) {
  const { data } = await wooClient.get('/products', { params });
  return data;
}

export async function getProduct(id) {
  const { data } = await wooClient.get(`/products/${id}`);
  return data;
}

export async function getProductCategories(params = {}) {
  const { data } = await wooClient.get('/products/categories', { params });
  return data;
}

/**
 * Looks up a product by exact SKU (e.g. "build-box-8"). Used to resolve
 * the real WooCommerce product ID for a Build Your Own Box size without
 * hardcoding numeric IDs in the frontend — the product can be recreated
 * in wp-admin at any time as long as the SKU convention is kept.
 * Returns null if no product has that SKU.
 */
export async function getProductBySku(sku) {
  const { data } = await wooClient.get('/products', { params: { sku } });
  return Array.isArray(data) && data.length > 0 ? data[0] : null;
}

// =====================================================================
// Store API — cart
// =====================================================================

export async function getCart() {
  const { data } = await wooClient.get('/cart');
  return data;
}

export async function addToCart({ id, quantity = 1, variationId, flavors } = {}) {
  const body = {
    id,
    quantity,
    variation_id: variationId,
  };
  // Read server-side by the raretto-headless plugin's
  // woocommerce_store_api_add_to_cart_data filter (Build Your Own Box) —
  // ignored for any product that isn't a build-box-* SKU.
  if (flavors) {
    body.raretto_flavors = JSON.stringify(flavors);
  }
  const { data } = await wooClient.post('/cart/add-item', body);
  return data;
}

export async function updateCartItem({ key, quantity }) {
  const { data } = await wooClient.post('/cart/update-item', { key, quantity });
  return data;
}

export async function removeCartItem(key) {
  const { data } = await wooClient.post('/cart/remove-item', { key });
  return data;
}

export async function applyCoupon(code) {
  const { data } = await wooClient.post('/cart/apply-coupon', { code });
  return data;
}

export async function removeCoupon(code) {
  const { data } = await wooClient.post('/cart/remove-coupon', { code });
  return data;
}

export async function updateCartCustomer(payload) {
  const { data } = await wooClient.post('/cart/update-customer', payload);
  return data;
}

export async function selectShippingRate(rateId) {
  const { data } = await wooClient.post('/cart/select-shipping-rate', {
    rate_id: rateId,
  });
  return data;
}

// =====================================================================
// Store API — checkout + order lookup
// =====================================================================

/**
 * GET /wc/store/v1/checkout
 * Returns draft order + available payment methods. Requires Cart-Token.
 */
export async function getCheckout() {
  const { data } = await wooClient.get('/checkout');
  return data;
}

/**
 * POST /wc/store/v1/checkout
 * Submits the order. The response is { order_id, status, payment_result }.
 * For Cashfree, payment_result.payment_status === "success" and
 * payment_result.redirect_url contains a URL to send the user to.
 */
export async function placeOrder(payload) {
  const { data } = await wooClient.post('/checkout', payload);
  return data;
}

/**
 * GET /wc/store/v1/order/:id?key=...&billing_email=... — public order lookup
 * by id. The Store API requires BOTH the order key and the billing email to
 * authorize a guest lookup (there's no PHP session to fall back on once
 * we're back on the React origin) — omitting either 401s.
 *
 * Note: this 403s with "belongs to a different customer" if the order got
 * attributed to a real WP customer_id instead of being a guest order (e.g.
 * whoever placed it was also logged into wp-admin in the same browser tab,
 * so their session cookie rode along on a same-origin request). See
 * trackOrder() below for a fallback that isn't affected by that.
 */
export async function getOrder(id, key, billingEmail) {
  const params = {};
  if (key) params.key = key;
  if (billingEmail) params.billing_email = billingEmail;
  const { data } = await wooClient.get(`/order/${id}`, { params });
  return data;
}

/**
 * POST /wp-json/raretto/v1/track-order — the custom guest order-lookup
 * endpoint (raretto-headless.php). Verifies order_id + email directly
 * against the order, independent of any customer_id/session — works for
 * guest orders and for orders tied to a real customer account alike.
 * Money fields come back in paise (minor units) as strings, same
 * convention as the Store API.
 */
export async function trackOrder(orderId, email) {
  try {
    const { data } = await wpClient.post('/raretto/v1/track-order', {
      order_id: orderId,
      email,
    });
    return data;
  } catch (err) {
    // wpClient has no response interceptor to normalize errors the way
    // wooClient does — do it here so callers get a real message ("We
    // could not find that order.") instead of axios's generic
    // "Request failed with status code 404".
    throw parseWooError(err);
  }
}

